// Space-theater furniture: the Brick Moon station and the two surface relays,
// plus the capture markers that say who holds them. Kept in its own module
// (the constitution's "gated on the theater, or in a theater-only module")
// so the other four theaters never load a line of it.
//
// Everything is built from the game's own vocabulary — merged boxes, flat
// vertex colours, no textures — because a station that doesn't look like it
// was laid brick by brick would be lying about its name.

import * as THREE from "three";

const box = (w, h, d, color) =>
  new THREE.Mesh(new THREE.BoxGeometry(w, h, d), new THREE.MeshLambertMaterial({ color }));

// Team colours for a capture marker: gold neutral, crimson, cobalt, violet
// contested — the same language every other theater's zone rings speak.
const HOLDER_COLOR = { "-1": 0xf4c542, 0: 0xe84a4a, 1: 0x4a7de8, 2: 0xc78ae0 };

/**
 * The Brick Moon: a wheel of brickwork and girders on the mid shell, turning
 * slowly, with a translucent bubble marking the volume you have to match
 * orbits inside to hold it. The first artificial satellite in all of fiction
 * was made of brick (Hale, 1869) and this one keeps the promise.
 */
function buildStation(radiusM) {
  const g = new THREE.Group();
  const RING_R = 46;
  const BRICK = 0x9d5b46, TRIM = 0xc9b79c, GIRDER = 0x6f7480, PANEL = 0x3f5d86;

  // The rim: bricks laid in a circle, every fourth one trimmed pale so the
  // wheel reads as masonry rather than a smooth torus.
  for (let i = 0; i < 28; i++) {
    const a = (i / 28) * Math.PI * 2;
    const b = box(15, 11, 9, i % 4 === 0 ? TRIM : BRICK);
    b.position.set(Math.cos(a) * RING_R, 0, Math.sin(a) * RING_R);
    b.rotation.y = -a;
    g.add(b);
  }
  // Four spokes and a hub — the pressurised core.
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    const s = box(RING_R, 4, 4, GIRDER);
    s.position.set((Math.cos(a) * RING_R) / 2, 0, (Math.sin(a) * RING_R) / 2);
    s.rotation.y = -a;
    g.add(s);
  }
  const hub = box(20, 16, 20, BRICK);
  g.add(hub);
  const cap = box(13, 6, 13, TRIM);
  cap.position.y = 10;
  g.add(cap);
  // Solar wings: the only thing out here that isn't masonry.
  for (const side of [-1, 1]) {
    const wing = box(3, 1.5, 34, PANEL);
    wing.position.set(side * 30, 12, 0);
    g.add(wing);
  }

  // The capture bubble: you must be inside this AND matched to it.
  const bubble = new THREE.Mesh(
    new THREE.SphereGeometry(radiusM, 20, 12),
    new THREE.MeshBasicMaterial({
      color: 0xf4c542, transparent: true, opacity: 0.055, depthWrite: false, side: THREE.BackSide,
    }),
  );
  g.add(bubble);
  return { group: g, bubble };
}

/**
 * A RELAY OUTPOST: the little sister of the wheel, and what the moon's two
 * surface relays became when the war stopped covering the whole moon.
 *
 * They used to be masts bolted to the regolith at opposite ends of the world,
 * and ground is the one thing that cannot keep station with an orbiting fight
 * — a relay would slide out the back of the arena a minute into every round.
 * So the dishes went up with everything else: a girder spine, the same
 * brickwork core, and the old lattice-and-dish silhouette kept on the end so
 * a pilot still reads them as the radio prize rather than a second wheel.
 */
function buildOutpost(radiusM) {
  const g = new THREE.Group();
  const BRICK = 0x9d5b46, TRIM = 0xc9b79c, STEEL = 0x8b8f96, DISH = 0xd8d4c8, PANEL = 0x3f5d86;

  // The spine: a short girder truss with a brick pressure can amidships.
  const spine = box(52, 3, 3, STEEL);
  g.add(spine);
  for (let i = -1; i <= 1; i += 2) {
    const brace = box(3, 12, 3, STEEL);
    brace.position.x = i * 16;
    g.add(brace);
  }
  const can = box(18, 13, 13, BRICK);
  g.add(can);
  const collar = box(6, 15, 15, TRIM);
  collar.position.x = 9;
  g.add(collar);
  // The dish it is named for, still aimed off the beam.
  const dish = box(20, 20, 4, DISH);
  dish.position.set(-26, 0, 0);
  dish.rotation.y = 0.5;
  g.add(dish);
  const feed = box(9, 2, 2, STEEL);
  feed.position.set(-21, 0, 0);
  g.add(feed);
  // One wing, because an outpost is a smaller thing than a station.
  const wing = box(2, 1.5, 26, PANEL);
  wing.position.set(4, 9, 0);
  g.add(wing);

  // The same capture bubble as the wheel: match orbits inside it to hold it.
  const bubble = new THREE.Mesh(
    new THREE.SphereGeometry(radiusM, 18, 10),
    new THREE.MeshBasicMaterial({
      color: 0xf4c542, transparent: true, opacity: 0.055, depthWrite: false, side: THREE.BackSide,
    }),
  );
  g.add(bubble);
  return { group: g, bubble };
}

/**
 * All three prizes. `zones` is the welcome's zone list (centres + radii); the
 * first is the station and the rest are its outposts, and every one of them
 * rides the same shell.
 *
 * THE WIRE CARRIES ONE LONGITUDE. The group orbits, so the welcome cannot hand
 * over positions that stay true — but it does not have to hand over three
 * either, because the formation is RIGID: one shell means one orbital rate, so
 * the gaps the welcome describes are the gaps forever. We keep those offsets
 * at boot and hang all three off the station's live longitude, which is the
 * single f32 the snapshot already carried before any of this existed.
 */
export class SpaceProps {
  constructor(scene, field, zones) {
    this.scene = scene;
    this.wrapM = field.wrapM;
    this.spin = 0;
    const list = zones ?? [];
    const st = list[0] ?? { r: 500, y: 1600 };
    this.station = buildStation(st.r > 0 ? st.r : 500);
    this.stationY = st.y ?? 1600;
    scene.add(this.station.group);

    // Wrap-aware offset from the station to each outpost, frozen at boot.
    const wrapDelta = (a, b) => {
      if (!(this.wrapM > 0)) return a - b;
      return ((((a - b) % this.wrapM) + this.wrapM * 1.5) % this.wrapM) - this.wrapM * 0.5;
    };
    this.outposts = list.slice(1).map((z) => {
      const o = buildOutpost(z.r > 0 ? z.r : 260);
      o.dx = wrapDelta(z.x ?? 0, st.x ?? 0);
      o.dz = z.z ?? 0;
      scene.add(o.group);
      return o;
    });
  }

  /**
   * stationX is the group's live longitude off the wire; camX places it in
   * the nearest wrap image, because on a cylinder the station three metres
   * behind you and the station a whole map ahead are the same station.
   */
  update(dt, stationX, camX, holders, cam = null) {
    const g = this.station.group;
    if (stationX != null && this.wrapM > 0) {
      const k = Math.round((camX - stationX) / this.wrapM);
      const anchor = stationX + k * this.wrapM;
      g.position.set(anchor, this.stationY, 0);
      for (const o of this.outposts) o.group.position.set(anchor + o.dx, this.stationY, o.dz);
    }
    // OVER THE HORIZON, OUT OF SIGHT. Since the renderer bends the moon
    // (curveworld.js) the props have to obey the bulge like everything else,
    // and a zone marker is the worst offender if it doesn't: the station's
    // bubble is a 450 m cylinder, so once its centre passes the limb the
    // curvature folds the whole shell onto the horizon ring and it reads as a
    // huge dark disc hanging over the mare (verbatim from the first curved
    // screenshot). The horizon test is the same one the server spots by — two
    // points on a sphere see each other while the angle between them is under
    // acos(R/r1) + acos(R/r2) — so what the eye can see and what the fog of
    // war admits stay the same sky.
    if (cam) {
      const R = this.wrapM / (2 * Math.PI);
      const seen = (p) => {
        const dx = Math.abs(((p.x - cam.x + this.wrapM * 1.5) % this.wrapM) - this.wrapM * 0.5);
        const theta = Math.hypot(dx, p.z - cam.z) / R;
        const rc = R + Math.max(0, cam.y), rp = R + Math.max(0, p.y);
        return theta <= Math.acos(Math.min(1, R / rc)) + Math.acos(Math.min(1, R / rp));
      };
      g.visible = seen(g.position);
      for (const o of this.outposts) o.group.visible = seen(o.group.position);
    }
    this.spin += dt * 0.06; // a slow, dignified turn
    g.rotation.y = this.spin;
    // The outposts keep station, they don't pirouette: a slow yaw drift is
    // enough to say "unmanned and coasting" without competing with the wheel.
    for (let i = 0; i < this.outposts.length; i++) {
      this.outposts[i].group.rotation.y = this.spin * 0.35 + i * 1.7;
    }

    const hs = holders ?? [];
    const paint = (mesh, holder) => {
      const c = HOLDER_COLOR[String(holder ?? -1)] ?? HOLDER_COLOR["-1"];
      mesh.material.color.setHex(c);
    };
    paint(this.station.bubble, hs[0]);
    this.outposts.forEach((o, i) => paint(o.bubble, hs[i + 1]));
  }
}
